/**
 * @fileoverview Checkout Form Component
 *
 * This file contains the main CheckoutForm component that provides a dynamic,
 * configurable checkout form interface with validation and persistence features.
 *
 * Key Features:
 * - Dynamic form rendering based on API metadata and configuration
 * - Field validation with real-time error display
 * - Form persistence across browser sessions (24-hour expiration)
 * - Country-specific field logic (USA state selection handling)
 * - Collapsible sections for better UX
 * - Imperative API for external validation and data clearing
 * - Integration with checkout workflow and cart totals
 *
 * Component Structure:
 * - ForwardRef component exposing validation and persistence methods
 * - Dynamic field grouping and layout based on configuration
 * - Specialized field renderers for different input types
 * - Real-time validation with error feedback
 * - Conditional field display based on form state
 */

import React, { useEffect, useImperativeHandle, forwardRef } from "react";
import { Circle } from "better-react-spinkit";
import { CheckoutApiResponse, CheckoutFieldMetadata } from "./CheckoutForm.types";
import { useCheckoutForm, useCheckoutTotal } from "./CheckoutForm.hooks";
import {
  groupCheckoutFields,
  isUSASelected,
  isAutoSelectSiteEnabled,
  shouldDisableAddressField,
} from "./CheckoutForm.utils";
import { checkoutFormConfig } from "./CheckoutForm.config";
import {
  ReadOnlyField,
  TextInputField,
  SelectField,
  SiteSelectField,
  CurrencyDisplay,
} from "./CheckoutForm.fields";
import "./CheckoutForm.scss";

/**
 * Imperative API interface for CheckoutForm component
 * Provides methods for external components to interact with the form
 */
export interface CheckoutFormRef {
  /** Clears all persisted form data from localStorage */
  clearPersistedData: () => void;
  /** Validates the entire form and returns validation status */
  validateForm: () => Promise<boolean>;
}

/**
 * Props interface for CheckoutForm component
 */
interface CheckoutFormProps {
  /** Callback when order total is calculated or updated */
  onTotalUpdate: (total: number) => void;
  /** Optional callback when form data changes */
  onFormDataChange?: (formData: Record<string, any>) => void;
  /** Checkout data from API containing form metadata and initial values */
  checkoutData: CheckoutApiResponse | null;
  /** Whether the form is in a loading state */
  loading?: boolean;
  /** Error message to display if form loading failed */
  error?: string | null;
}

/**
 * CheckoutForm Component
 *
 * A dynamic checkout form that renders fields based on API metadata and handles
 * form validation, persistence, and submission preparation.
 *
 * @param props - Component props including callbacks and checkout data
 * @param ref - Forward ref for imperative API access
 * @returns JSX element for the complete checkout form
 */
const CheckoutForm = forwardRef<CheckoutFormRef, CheckoutFormProps>(
  ({ onTotalUpdate, onFormDataChange, checkoutData, loading = false, error = null }, ref) => {
    const {
      formData,
      collapsedSections,
      handleFieldChange,
      getFieldValue,
      toggleCollapsibleSection,
      validateForm,
      getFieldError,
      clearFormData,
    } = useCheckoutForm(checkoutData);

    useCheckoutTotal(checkoutData, onTotalUpdate);

    // Expose clearFormData function via ref
    useImperativeHandle(ref, () => ({
      clearPersistedData: clearFormData,
      validateForm: validateForm,
    }));

    // Notify parent component when form data changes
    useEffect(() => {
      if (onFormDataChange) {
        onFormDataChange(formData);
      }
    }, [formData, onFormDataChange]);

    if (loading) {
      return (
        <div className="CheckoutForm">
          <div className="LoadingState">
            <Circle size={50} color="#ff5a00" scaleStart={0.4} scaleEnd={0.8} />
            <p>Loading checkout form...</p>
          </div>
        </div>
      );
    }

    if (error || !checkoutData) {
      return (
        <div className="CheckoutForm">
          <div className="ErrorState">
            <p className="ErrorMessage">
              {error || "Something went wrong loading the checkout form."}
            </p>
            <button className="RetryButton" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        </div>
      );
    }

    // Group fields by their groupLabel
    const groupedFields = groupCheckoutFields(checkoutData.metadata);

    const renderField = (field: CheckoutFieldMetadata, className?: string) => {
      const value = getFieldValue(field);
      const error = getFieldError(field.name);

      // Check if auto-select site mode is enabled
      const isAutoSiteMode = isAutoSelectSiteEnabled(checkoutData);

      // Helper function to check if selected country is USA
      const isUSA = isUSASelected(checkoutData, getFieldValue);

      // Hide SitesId field when NOT in auto-select site mode
      if (field.name === "SitesId" && !isAutoSiteMode) {
        return null;
      }

      // Special handling for SitesId field when in auto-select site mode
      if (field.name === "SitesId" && isAutoSiteMode) {
        const props = {
          field,
          value,
          onChange: handleFieldChange,
          data: checkoutData.details,
          className,
          error,
        };
        return <SiteSelectField key={field.name} {...props} />;
      }

      // Hide SitesName field when in auto-select site mode (it's filled automatically)
      if (field.name === "SitesName" && isAutoSiteMode) {
        return null;
      }

      // Special handling for StateProvince field based on country selection
      if (field.name === "StateProvince") {
        if (isUSA) {
          // Find the USStatesID field to use its dataSource
          const usStatesField = checkoutData.metadata.find((f) => f.name === "USStatesID");
          if (usStatesField) {
            const countryValue = getFieldValue({ name: "CountriesId" } as CheckoutFieldMetadata);
            const usStatesIDValue = getFieldValue({ name: "USStatesID" } as CheckoutFieldMetadata);
            const stateProvinceValue = value;

            const stateProps = {
              field: {
                ...field,
                dataSource: usStatesField.dataSource,
                type: "SingleSelect" as const,
              },
              value: usStatesIDValue || stateProvinceValue,
              onChange: (fieldName: string, val: any) => {
                // Update both StateProvince and USStatesID
                handleFieldChange("USStatesID", val);
              },
              data: checkoutData.details,
              className,
              disabled: shouldDisableAddressField(checkoutData, "StateProvince"),
              error: getFieldError("StateProvince") || getFieldError("USStatesID"),
            };
            // Force re-render by adding country value to key
            return <SelectField key={`${field.name}-${countryValue}`} {...stateProps} />;
          }
        }
      }

      // Skip USStatesID field completely - it's handled within StateProvince when USA is selected
      if (field.name === "USStatesID") {
        return null; // Always hide this field since we handle it in StateProvince
      }

      // Check if this address field should be disabled due to auto-select site mode
      const shouldDisable = shouldDisableAddressField(checkoutData, field.name);

      const props = {
        field,
        value,
        onChange: handleFieldChange,
        data: checkoutData.details,
        className,
        error,
      };

      switch (field.type) {
        case "StringReadOnly":
          return <ReadOnlyField key={field.name} {...props} />;
        case "String":
          // Render address fields as readonly when disabled by site selection
          if (shouldDisable) {
            return <ReadOnlyField key={field.name} {...props} />;
          }
          return <TextInputField key={field.name} {...props} />;
        case "SingleSelect":
          // Render address selects as readonly when disabled by site selection
          if (shouldDisable) {
            // For select fields, we need to show the display value rather than the ID
            let displayValue = value;
            if (field.dataSource === "ListOfCountries") {
              const country = checkoutData.details.listOfCountries.find((c) => c.value == value);
              displayValue = country?.label || value;
            } else if (field.dataSource === "ListOfUSStates") {
              const state = checkoutData.details.listOfUSStates.find((s) => s.value == value);
              displayValue = state?.label || value;
            }
            return <ReadOnlyField key={field.name} {...{ ...props, value: displayValue }} />;
          }
          return <SelectField key={field.name} {...props} />;
        case "Currency":
          return <CurrencyDisplay key={field.name} {...props} />;
        default:
          return <TextInputField key={field.name} {...props} />;
      }
    };

    const renderGroup = (groupLabel: string, fields: CheckoutFieldMetadata[]) => {
      const groupConfig = checkoutFormConfig.groups[groupLabel];
      if (!groupConfig) return null;

      // Skip Order Summary group - it's handled by cart total update
      if (groupConfig.type === "display" && groupConfig.useForCartTotal) {
        return null;
      }

      const processedFields = new Set<string>();

      const renderGroupContent = () => {
        const content: React.ReactNode[] = [];

        if (Array.isArray(groupConfig.fields)) {
          // Simple array of field names
          groupConfig.fields.forEach((fieldName) => {
            const field = fields.find((f) => f.name === fieldName);
            if (field && !processedFields.has(field.name)) {
              content.push(renderField(field));
              processedFields.add(field.name);
            }
          });
        } else {
          // Complex object with layout rules
          Object.entries(groupConfig.fields).forEach(([key, config]) => {
            if (config.row) {
              // Render fields in a row
              const rowFields = config.row
                .map((fieldName) => fields.find((f) => f.name === fieldName))
                .filter((field) => field && !processedFields.has(field.name));

              if (rowFields.length > 0) {
                content.push(
                  <div key={key} className="FormRow">
                    {rowFields.map((field) => {
                      processedFields.add(field!.name);
                      return renderField(field!, "RowField");
                    })}
                  </div>
                );
              }
            } else if (config.fields && config.collapsible) {
              // Render collapsible section
              const sectionFields = config.fields
                .map((fieldName) => fields.find((f) => f.name === fieldName))
                .filter((field) => field && !processedFields.has(field.name));

              if (sectionFields.length > 0) {
                // Mark all section fields as processed immediately
                sectionFields.forEach((field) => {
                  if (field) processedFields.add(field.name);
                });

                const isCollapsed = collapsedSections[key] ?? true;
                content.push(
                  <div key={key} className="CollapsibleSection">
                    <button
                      type="button"
                      className="CollapsibleToggle"
                      onClick={() => toggleCollapsibleSection(key)}
                    >
                      <span className={`ToggleIcon ${isCollapsed ? "collapsed" : ""}`}>▼</span>
                      {config.title || key}
                    </button>
                    {!isCollapsed && (
                      <div className="CollapsibleContent">
                        {sectionFields.map((field) => {
                          return renderField(field!);
                        })}
                      </div>
                    )}
                  </div>
                );
              }
            } else {
              // Single field with specific config
              const field = fields.find((f) => f.name === key);
              if (field && !processedFields.has(field.name)) {
                const className = config.width === "full" ? "FullWidth" : "";
                content.push(renderField(field, className));
                processedFields.add(field.name);
              }
            }
          });
        }

        // Render any remaining fields that weren't processed
        fields.forEach((field) => {
          if (!processedFields.has(field.name)) {
            content.push(renderField(field));
          }
        });

        return content;
      };

      return (
        <div key={groupLabel} className="FormSection">
          <h3 className="SectionTitle">{groupLabel}</h3>
          {renderGroupContent()}
        </div>
      );
    };

    return (
      <div className="CheckoutForm">
        <form>
          {Object.entries(groupedFields).map(([groupLabel, fields]) =>
            renderGroup(groupLabel, fields)
          )}
        </form>
      </div>
    );
  }
);

CheckoutForm.displayName = "CheckoutForm";

export default CheckoutForm;
